import { useState } from 'react';
import { Button, Pill } from '@salonomy/ui';
import { ConfirmModal } from '../../components/ConfirmModal.js';
import { useEraseBook, useExportBook } from '../../data/gdpr.js';
import { useToast } from '../../lib/toast.js';
import { downloadJson, exportFilename } from './gdpr-download.js';

/**
 * Whole-book GDPR (per-renter portability + erasure). What "the book" means depends on the session,
 * mirroring the API exactly:
 *   - chair renter  → their OWN sealed book (RLS-scoped). They can export AND erase it (the
 *     "leaving the chair / right to be forgotten" path).
 *   - owner/manager → the SHARED salon book. They can export it; there is no "erase the whole shared
 *     book" button — a renter's sealed book is unreachable from here by design.
 *
 * Erasure is ONLINE-ONLY with explicit success: a typed-confirm phrase, a live mutation (never the
 * offline outbox), and a clear count-of-deleted success message.
 */
export function BookGdprControls({
  isRenter,
  bookLabel,
}: {
  /** True when the session owns a sealed book (user.renterId != null) — enables book erasure. */
  isRenter: boolean;
  /** The typed-confirm phrase for whole-book erasure — the renter's own display name. */
  bookLabel: string;
}) {
  const toast = useToast();
  const exportBook = useExportBook();
  const eraseBook = useEraseBook();
  const [confirmErase, setConfirmErase] = useState(false);
  const [erasing, setErasing] = useState(false);

  async function onExport() {
    try {
      const bundle = await exportBook.mutateAsync();
      const scopeLabel = bundle.scope === 'sealed' ? 'sealed-book' : 'shared-book';
      downloadJson(exportFilename(`${scopeLabel}-${bundle.clients.length}-clients`), bundle);
      toast.success(
        `Exported ${bundle.clients.length} client${bundle.clients.length === 1 ? '' : 's'}`,
      );
    } catch {
      toast.error('Could not export the book — check your connection and try again.');
    }
  }

  async function onConfirmErase() {
    setErasing(true);
    try {
      const { deleted } = await eraseBook.mutateAsync();
      toast.success(`Your book is erased — ${deleted} client${deleted === 1 ? '' : 's'} deleted`);
      setConfirmErase(false);
    } catch {
      toast.error('Erasure failed — nothing was deleted. This action is online-only; try again.');
    } finally {
      setErasing(false);
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ fontWeight: 600 }}>{isRenter ? 'Your client book' : 'Shared salon book'}</div>
        {isRenter ? <Pill tone="soft">Sealed — only you can see it</Pill> : null}
      </div>
      <div className="muted" style={{ fontSize: 13, margin: '4px 0 12px' }}>
        {isRenter
          ? 'Export your whole book to take it with you, or erase it entirely if you’re leaving. Your clients are sealed — no one else in the salon can see or export them.'
          : 'Export the whole shared book as a machine-readable file. Each chair renter’s sealed book is theirs alone and cannot be exported or erased from here.'}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button variant="outline" onClick={onExport} disabled={exportBook.isPending}>
          {exportBook.isPending ? 'Exporting…' : `Export ${isRenter ? 'my book' : 'shared book'}`}
        </Button>

        {isRenter ? (
          <Button variant="danger" onClick={() => setConfirmErase(true)}>
            Erase my book
          </Button>
        ) : (
          <span
            className="muted"
            style={{ fontSize: 12 }}
            title="The shared book belongs to the salon. A renter’s sealed book can only be erased by that renter."
          >
            No whole-book erasure for the shared book.
          </span>
        )}
      </div>

      <ConfirmModal
        open={confirmErase}
        title="Erase your entire client book"
        confirmLabel="Erase my whole book"
        confirmPhrase={bookLabel}
        busy={erasing}
        onConfirm={onConfirmErase}
        onClose={() => setConfirmErase(false)}
      >
        <p style={{ margin: '0 0 8px' }}>
          This permanently deletes <strong>every client in your sealed book</strong> — all profiles,
          appointment history and notes. It cannot be undone, and it affects only your book.
        </p>
        <p style={{ margin: 0 }}>
          This runs online only and confirms the moment it completes — it is never queued to sync
          later. Export your book first if you want a copy.
        </p>
      </ConfirmModal>
    </div>
  );
}
